/**
 * TilesetLoader — loads and validates the tileset.json configuration.
 *
 * tileset.json lives alongside the tile sprites in public/assets/tilesets/.
 * It defines per-tile metadata:
 *   - id:             Unique string identifier (e.g. "grass")
 *   - index:          1-based tile index (left-to-right, top-to-bottom in the grid)
 *   - row / col:      Grid coordinates (informational; index is authoritative)
 *   - walkable:       Whether entities can walk on this tile
 *   - collisionShape: "none" | "full" | "slope_*" (for future)
 *   - weight:         Relative probability weight for WFC tile selection
 *   - edges:          Adjacency rules — N/E/S/W lists of allowed neighbour ids
 *
 * Top-level fields:
 *   - columns:        Number of columns in the sprite sheet grid
 *   - tileWidth:      Tile width in pixels
 *   - tileHeight:     Tile height in pixels
 */

export type CollisionShape = 'none' | 'full' | `slope_${string}`;

export interface TileDefinition {
  id: string;
  index: number;
  /** Grid row (0-based, informational) */
  row?: number;
  /** Grid column (0-based, informational) */
  col?: number;
  walkable: boolean;
  collisionShape: CollisionShape;
  weight: number;
  /** Movement speed multiplier for this tile. Values > 1 slow movement. Default 1.0. */
  movementMultiplier?: number;
  /**
   * Per-pixel collision mask for sub-tile precision.
   * 32 uint32 values — bit N of row R indicates pixel (col=N, row=R) is solid.
   * Absent = use collisionShape to infer (full or none).
   */
  collisionMask?: number[];
  edges: {
    N: string[];
    E: string[];
    S: string[];
    W: string[];
  };
}

export interface TilesetConfig {
  /** Number of columns in the sprite sheet (used for documentation; Phaser reads the image directly). */
  columns?: number;
  tileWidth?: number;
  tileHeight?: number;
  tiles: TileDefinition[];
}

/** In-memory cache so we only parse once per session. */
let cachedConfig: TilesetConfig | null = null;

/**
 * Load and validate the tileset config from a plain object (already parsed JSON).
 * Call this once during preload with the JSON fetched via Phaser's load pipeline.
 */
export function loadTilesetConfig(raw: unknown): TilesetConfig {
  if (cachedConfig) return cachedConfig;

  if (
    typeof raw !== 'object' ||
    raw === null ||
    !Array.isArray((raw as Record<string, unknown>).tiles)
  ) {
    throw new Error('TilesetLoader: invalid tileset.json — missing "tiles" array');
  }

  const config = raw as TilesetConfig;

  // Basic validation
  for (const tile of config.tiles) {
    if (!tile.id) throw new Error(`TilesetLoader: tile missing "id"`);
    if (typeof tile.index !== 'number')
      throw new Error(`TilesetLoader: tile "${tile.id}" missing numeric "index"`);
    if (typeof tile.walkable !== 'boolean')
      throw new Error(`TilesetLoader: tile "${tile.id}" missing "walkable"`);
    if (!tile.collisionShape)
      throw new Error(`TilesetLoader: tile "${tile.id}" missing "collisionShape"`);
    if (!tile.edges?.N || !tile.edges?.E || !tile.edges?.S || !tile.edges?.W)
      throw new Error(`TilesetLoader: tile "${tile.id}" missing "edges" (N/E/S/W)`);
    if (typeof tile.weight !== 'number' || tile.weight <= 0) {
      tile.weight = 1; // default
    }
  }

  cachedConfig = config;
  return config;
}

/**
 * Clear the cache (useful for hot-reload / testing).
 */
export function clearTilesetCache(): void {
  cachedConfig = null;
}

/**
 * Get walkability for a given tile index (1-based).
 * Returns true if walkable, false otherwise.
 * Defaults to false for unknown tiles (safe for collision).
 */
export function isTileWalkable(config: TilesetConfig, tileIndex: number): boolean {
  const def = config.tiles.find((t) => t.index === tileIndex);
  return def ? def.walkable : false;
}

/**
 * Get collision shape for a given tile index.
 */
export function getTileCollisionShape(
  config: TilesetConfig,
  tileIndex: number
): CollisionShape {
  const def = config.tiles.find((t) => t.index === tileIndex);
  return def ? def.collisionShape : 'full';
}

/**
 * Get movement speed multiplier for a given tile index.
 * Values > 1 slow movement; values < 1 speed it up.
 * Defaults to 1.0 for unknown tiles.
 */
export function getTileMovementMultiplier(config: TilesetConfig, tileIndex: number): number {
  const def = config.tiles.find((t) => t.index === tileIndex);
  return def?.movementMultiplier ?? 1.0;
}

// ── Per-pixel collision mask helpers ─────────────────────────────────────────

/** Solid mask — every pixel in a 32×32 tile is blocked. */
const MASK_FULL: number[] = Array.from({ length: 32 }, () => 0xffffffff);
/** Empty mask — every pixel in a 32×32 tile is open. */
const MASK_EMPTY: number[] = Array.from({ length: 32 }, () => 0);

/**
 * Return the 32-element collision mask for a given tile index.
 * Falls back to full (solid) or empty (open) based on collisionShape when no
 * explicit mask is present.
 */
export function getCollisionMask(config: TilesetConfig, tileIndex: number): number[] {
  const def = config.tiles.find((t) => t.index === tileIndex);
  if (!def) return MASK_FULL;
  if (def.collisionMask && def.collisionMask.length === 32) return def.collisionMask;
  if (def.collisionShape === 'none') return MASK_EMPTY;
  return MASK_FULL;
}

/**
 * Sample a single pixel in a 32×32 collision mask.
 * Returns true (solid) for out-of-bounds coordinates.
 */
export function sampleCollisionMask(mask: number[], pixelX: number, pixelY: number): boolean {
  if (pixelY < 0 || pixelY >= 32 || pixelX < 0 || pixelX >= 32) return true;
  return ((mask[pixelY] >>> pixelX) & 1) === 1;
}
